/**
 * Pure helpers that decide how the page form presents a template's fields: their order and
 * whether stored page data fills them.
 */
import {gridFieldKeysForContext, inferLayoutContexts} from './page.core.js';
import {FieldType, PageKey, TABLE_ROW_ID_KEY, altKey, isTruthyFlag} from '../vocabulary.js';

/** Pebble keywords, which never identify a field inside a template tag. */
const PEBBLE_KEYWORDS = new Set([
    'if', 'else', 'elseif', 'endif',
    'for', 'endfor', 'in',
    'set', 'include', 'extends', 'block', 'endblock',
    'macro', 'endmacro', 'import', 'from', 'with', 'as', 'do',
    'autoescape', 'endautoescape', 'filter', 'endfilter', 'flush',
    'embed', 'endembed', 'cache', 'endcache', 'parallel', 'endparallel',
    'and', 'or', 'not', 'is',
    'true', 'false', 'null', 'none',
]);

/**
 * Offset of the first `{{ }}` or `{% %}` tag that mentions each field key.
 * @param {string} content
 * @param {string[]} fieldKeys
 * @returns {Map<string, number>}
 */
function contentOrderIndex(content, fieldKeys) {
    const result = new Map();
    if (!content || fieldKeys.length === 0) return result;

    const keyRegexes = [];
    for (const key of fieldKeys) {
        if (!key || PEBBLE_KEYWORDS.has(key)) continue;
        const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        keyRegexes.push([key, new RegExp(`\\b${escaped}\\b`)]);
    }
    if (keyRegexes.length === 0) return result;

    const tagPattern = /\{[{%][\s\S]*?[}%]\}/g;
    for (const match of content.matchAll(tagPattern)) {
        for (const [key, regex] of keyRegexes) {
            if (result.has(key)) continue;
            if (regex.test(match[0])) {
                result.set(key, match.index);
            }
        }
        if (result.size === keyRegexes.length) break;
    }
    return result;
}

/**
 * Fields in the order the form shows them: the slug first, then content fields in the
 * order the template content first mentions them (unmentioned ones by label), then the grid
 * position fields. An explicit alt-text field follows its image.
 *
 * @param {import('../../shell/types/user.types.js').TemplateField[]} fields
 * @param {string} templateContent
 * @param {import('../../shell/types/user.types.js').TemplateLayoutConfig | null} layoutConfig
 * @returns {import('../../shell/types/user.types.js').TemplateField[]}
 */
export function orderFormFields(fields, templateContent, layoutConfig) {
    const gridKeys = new Set(inferLayoutContexts(fields, layoutConfig).flatMap(gridFieldKeysForContext));
    const nonGridKeys = fields
        .filter(f => !gridKeys.has(f.key))
        .map(f => f.key);
    const contentIndex = contentOrderIndex(templateContent, nonGridKeys);
    const contentIndexFor = (key) => contentIndex.get(key) ?? Number.MAX_SAFE_INTEGER;
    const sorted = [...fields].sort((a, b) => {
        // The slug is the page's URL identity, so it leads wherever the content mentions it.
        if (a.key === PageKey.SLUG && b.key !== PageKey.SLUG) return -1;
        if (b.key === PageKey.SLUG && a.key !== PageKey.SLUG) return 1;
        const aIsGrid = gridKeys.has(a.key);
        const bIsGrid = gridKeys.has(b.key);
        if (aIsGrid && bIsGrid) return 0;
        if (aIsGrid) return 1;
        if (bIsGrid) return -1;
        const aIdx = contentIndexFor(a.key);
        const bIdx = contentIndexFor(b.key);
        if (aIdx !== bIdx) return aIdx - bIdx;
        return a.label.localeCompare(b.label);
    });

    const reordered = [];
    const consumed = new Set();
    for (const field of sorted) {
        if (consumed.has(field.key)) continue;
        reordered.push(field);
        consumed.add(field.key);

        if (field.type === FieldType.IMAGE) {
            const imageAltKey = altKey(field.key);
            const altField = sorted.find(f => f.key === imageAltKey);
            if (altField && !consumed.has(imageAltKey)) {
                reordered.push(altField);
                consumed.add(imageAltKey);
            }
        }
    }
    return reordered;
}

/**
 * Whether stored page data gives a field content worth showing: a checked checkbox, a table
 * with a non-blank cell, or any other non-blank value. A field that only shows while another
 * field has a value is empty while that field is.
 *
 * @param {import('../../shell/types/user.types.js').TemplateField | undefined} field
 * @param {string} key
 * @param {Object<string, *>} data
 * @returns {boolean}
 */
export function hasStoredContent(field, key, data) {
    const value = data[key];
    if (field?.dependsOn && !String(data[field.dependsOn] ?? '').trim()) return false;
    if (field?.type === FieldType.CHECKBOX) {
        return isTruthyFlag(value);
    }
    if (field?.type === FieldType.TABLE) {
        return Array.isArray(value) && value.some((row) =>
            Object.entries(row || {}).some(([cellKey, cell]) =>
                cellKey !== TABLE_ROW_ID_KEY && String(cell ?? '').trim())
        );
    }
    return value != null && String(value).trim() !== '';
}
